import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Center,
    Flex,
    HStack,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Button,
  ComposeSendButton,
  Icon,
  IconButton,
  TextArea,
  TextField,
  Tooltip,
} from '../../../ui';
import {
  useMarkAllNegotiationsRead,
  useMarkNegotiationRead,
  useNegotiationFeed,
  useNegotiationReply,
  useNegotiationThread,
  useNegotiationUnreadCount,
} from '../../../hooks/invoice/useInvoiceNegotiation';
import type { NegotiationFeedRow, NegotiationMessage } from '../../../types/invoice/negotiation';
import SupplierOrderInfoPopover from '../../../components/SupplierOrderInfoPopover';
import SupplierOrderSnapshotModal from '../../../modals/SupplierOrderSnapshotModal';
import { useGetOrderDetail } from '../../../hooks/useGetOrderDetail';
import { keyframes } from '@emotion/react';

const formatFeedDate = (s: string) => {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const date = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
  return `${time} • ${date}`;
};

const formatMessageTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const sparklePulse = keyframes`
  0%, 100% { transform: scale(1) rotate(-8deg); opacity: 0.75; }
  50% { transform: scale(1.18) rotate(8deg); opacity: 1; }
`;

const CarlThinkingRow = () => (
  <HStack spacing={2} alignSelf="flex-end" px={1} py={2}>
    <Icon
      name="sparkles"
      color="brand.solid"
      size="md"
      sx={{ animation: `${sparklePulse} 1.3s ease-in-out infinite` }}
    />
    <HStack spacing={1}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="6px"
          h="6px"
          borderRadius="full"
          bg="brand.solid"
          opacity={0.45}
          sx={{ animation: `${sparklePulse} 1.1s ease-in-out ${i * 0.15}s infinite` }}
        />
      ))}
    </HStack>
    <Text fontSize="xs" color="fg.muted">
      Carl is thinking…
    </Text>
  </HStack>
);

const MessageBubble = ({ message }: { message: NegotiationMessage }) => {
  const isVendor = message.role === 'vendor';
  const label =
    message.role === 'carl'
      ? 'Carl'
      : message.role === 'vendor'
        ? (message.senderDisplayName || 'Vendor')
        : (message.senderDisplayName || 'You');

  return (
    <Flex justify={isVendor ? 'flex-start' : 'flex-end'} w="100%">
      <VStack
        align={isVendor ? 'flex-start' : 'flex-end'}
        spacing={1}
        maxW="75%"
      >
        <HStack spacing={2}>
          <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
            {label}
          </Text>
          <Text fontSize="11px" color="fg.muted">
            {formatMessageTime(message.sentAt)}
          </Text>
        </HStack>
        <Box
          bg={isVendor ? 'bg.surface' : 'brand.solid'}
          color={isVendor ? 'fg.default' : 'fg.onBrand'}
          borderWidth={isVendor ? '1px' : 0}
          borderColor="border.default"
          borderRadius="lg"
          px={3}
          py={2}
          whiteSpace="pre-wrap"
          fontSize="sm"
        >
          {message.body}
        </Box>
        {message.attachments && message.attachments.length > 0 && (
          <VStack align={isVendor ? 'flex-start' : 'flex-end'} spacing={1} w="100%">
            {message.attachments.map((att) => (
              <Button
                key={`${att.url}-${att.filename}`}
                size="xs"
                variant="outlineGray"
                leftIcon={<Icon name="fileText" size="sm" />}
                onClick={() => window.open(att.url, '_blank', 'noopener,noreferrer')}
              >
                {att.filename}
              </Button>
            ))}
          </VStack>
        )}
      </VStack>
    </Flex>
  );
};

interface NegotiationThreadPaneProps {
  negotiationId: string | null;
  onOpened?: (negotiationId: string) => void;
}

export function NegotiationThreadPane({ negotiationId, onOpened }: NegotiationThreadPaneProps) {
  const navigate = useNavigate();
  const { data: thread, isLoading, isError: isThreadError } = useNegotiationThread(negotiationId);
  const replyMutation = useNegotiationReply();
  const [draft, setDraft] = useState('');
  const [isSnapshotOpen, setIsSnapshotOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const negotiationIdRef = useRef(negotiationId);
  negotiationIdRef.current = negotiationId;

  const { data: orderDetail } = useGetOrderDetail(thread?.orderId ?? '', {
    enabled: !!thread?.orderId,
  });

  const activityKey = thread
    ? `${thread.status}:${thread.messages.at(-1)?.id ?? ''}:${thread.messages.length}`
    : '';

  useEffect(() => {
    if (negotiationId) onOpened?.(negotiationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiationId, activityKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length, thread?.status]);

  useEffect(() => {
    setDraft('');
  }, [negotiationId]);

  const supplierSection = useMemo(() => {
    if (!thread?.supplierOrderId || !orderDetail) return undefined;
    for (const tab of orderDetail.ordered) {
      const match = tab.sections.find((s) => s.key === thread.supplierOrderId);
      if (match) return match;
    }
    return undefined;
  }, [orderDetail, thread?.supplierOrderId]);

  if (!negotiationId) {
    return (
      <Flex flex={1} align="center" justify="center" h="100%" bg="bg.subtle">
        <VStack spacing={3}>
          <Flex
            align="center"
            justify="center"
            w="56px"
            h="56px"
            borderRadius="full"
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.default"
          >
            <Icon name="message" size="lg" color="brand.solid" />
          </Flex>
          <VStack spacing={0.5}>
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
              No negotiation selected
            </Text>
            <Text fontSize="xs" color="fg.muted">
              Choose a thread on the left to view messages
            </Text>
          </VStack>
        </VStack>
      </Flex>
    );
  }

  if (isLoading && !thread) {
    return (
      <Flex h="100%" align="center" justify="center">
        <Spinner color="brand.solid" />
      </Flex>
    );
  }

  if (isThreadError && !thread) {
    return (
      <Center h="100%" px={4}>
        <VStack spacing={1}>
          <Text fontSize="sm" fontWeight="semibold" color="fg.default" textAlign="center">
            Couldn't load this negotiation
          </Text>
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            Refresh the page to try again
          </Text>
        </VStack>
      </Center>
    );
  }

  if (!thread) {
    return (
      <Center h="100%">
        <Text fontSize="sm" color="fg.muted">
          Negotiation not found
        </Text>
      </Center>
    );
  }

  const handleSend = () => {
    const body = draft.trim();
    if (!body || !thread.canReply || replyMutation.isPending) return;
    const submittedId = thread.negotiationId;
    const submittedBody = body;
    replyMutation.mutate(
      { negotiationId: submittedId, body: submittedBody },
      {
        onSuccess: () => {
          setDraft((current) =>
            negotiationIdRef.current === submittedId && current.trim() === submittedBody
              ? ''
              : current,
          );
        },
      },
    );
  };

  return (
    <Box h="100%" display="flex" flexDirection="column" bg="bg.surface" minH={0}>
      <Box px={5} py={3} borderBottomWidth="1px" borderColor="border.subtle" flexShrink={0}>
        <Flex justify="space-between" align="center" gap={3}>
          <HStack spacing={2} minW={0} flexWrap="wrap">
            <Text fontSize="md" fontWeight="semibold" color="fg.default" noOfLines={1}>
              {thread.supplierText || 'Supplier'}
            </Text>
            <Text
              as={RouterLink}
              to={`/invoices/${thread.invoiceId}`}
              fontSize="sm"
              fontWeight="medium"
              color="brand.solid"
              _hover={{ textDecoration: 'underline' }}
            >
              Inv # {thread.invoiceNumber || '—'}
            </Text>
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            <SupplierOrderInfoPopover
              label="Contact Info"
              phone={supplierSection?.phoneNumber}
              email={thread.supplierEmail ?? supplierSection?.email ?? undefined}
            />
            <Button
              size="xs"
              variant="ghostGray"
              leftIcon={<Icon name="fileText" size="sm" />}
              onClick={() => navigate(`/invoices/${thread.invoiceId}`)}
            >
              View Invoice
            </Button>
            <Button
              size="xs"
              variant="ghostGray"
              leftIcon={<Icon name="externalLink" size="sm" />}
              onClick={() => {
                if (supplierSection) setIsSnapshotOpen(true);
                else if (thread.orderId) window.open(`/order/${thread.orderId}`, '_blank');
              }}
            >
              View Purchase Order
            </Button>
          </HStack>
        </Flex>
        <VStack align="stretch" spacing={0.5} mt={2}>
          <Text fontSize="xs" color="fg.muted" noOfLines={1}>
            Subject: {thread.emailSubject || '—'}
          </Text>
          <Text fontSize="xs" color="fg.muted" noOfLines={1}>
            To: {thread.emailTo || '—'}
          </Text>
        </VStack>
      </Box>

      <VStack
        flex={1}
        minH={0}
        overflowY="auto"
        spacing={3}
        align="stretch"
        px={4}
        py={4}
        bg="bg.subtle"
      >
        {thread.messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {thread.status === 'processing' && <CarlThinkingRow />}
        <div ref={bottomRef} />
      </VStack>

      <Box px={4} py={3} borderTopWidth="1px" borderColor="border.subtle" flexShrink={0}>
        {thread.canReply ? (
          <Box>
            <Box position="relative">
              <TextArea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Reply to vendor…"
                size="sm"
                resize="none"
                minH="80px"
                pr="44px"
                pb="36px"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <ComposeSendButton
                aria-label="Send reply"
                isDisabled={!draft.trim()}
                isLoading={replyMutation.isPending}
                onClick={handleSend}
              />
            </Box>
            <Text fontSize="xs" color="fg.muted" mt={1.5} ml={1}>
              Press Ctrl+Enter to send
            </Text>
          </Box>
        ) : (
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {thread.status === 'processing'
              ? 'Carl is responding…'
              : thread.status === 'awaiting_reply'
                ? 'Waiting for the vendor to reply.'
                : thread.status === 'completed'
                  ? 'This negotiation is completed.'
                  : thread.status === 'refused'
                    ? 'The vendor refused to correct pricing.'
                    : 'Replies are disabled for this thread.'}
          </Text>
        )}
      </Box>

      <SupplierOrderSnapshotModal
        isOpen={isSnapshotOpen}
        onClose={() => setIsSnapshotOpen(false)}
        section={supplierSection}
        vin={orderDetail?.vin ?? ''}
        roNumber={orderDetail?.roNumber ?? ''}
        ymm={orderDetail?.vehicleName ?? ''}
      />
    </Box>
  );
}

interface NegotiationFeedViewProps {
  selectedNegotiationId: string | null;
  onSelectNegotiation: (negotiationId: string) => void;
  initialSearch?: string;
}

export function NegotiationFeedView({
  selectedNegotiationId,
  onSelectNegotiation,
}: NegotiationFeedViewProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [locallyRead, setLocallyRead] = useState<Set<string>>(new Set());

  const markRead = useMarkNegotiationRead();
  const markAllRead = useMarkAllNegotiationsRead();
  const { data: unreadData } = useNegotiationUnreadCount(true);
  const globalUnreadCount = unreadData?.count ?? 0;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError: isFeedError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNegotiationFeed({
      search: debouncedSearch || undefined,
    });

  const rows = useMemo(() => {
    const seen = new Set<string>();
    const merged: NegotiationFeedRow[] = [];
    for (const page of data?.pages ?? []) {
      for (const item of page.negotiations) {
        if (seen.has(item.negotiationId)) continue;
        seen.add(item.negotiationId);
        merged.push(item);
      }
    }
    return merged;
  }, [data?.pages]);

  const isUnread = (row: NegotiationFeedRow) => row.unread && !locallyRead.has(row.negotiationId);

  const unreadRows = useMemo(
    () => rows.filter((row) => row.unread && !locallyRead.has(row.negotiationId)),
    [rows, locallyRead],
  );

  // Drop local suppression when an unselected thread becomes unread again on the
  // server (e.g. new handoff after we left it). Keep suppression for the selected
  // thread so the feed dot stays quiet until the post-ack refetch lands.
  useEffect(() => {
    setLocallyRead((prev) => {
      let changed = false;
      const next = new Set(prev);
      prev.forEach((id) => {
        if (id === selectedNegotiationId) return;
        const row = rows.find((r) => r.negotiationId === id);
        if (row?.unread) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rows, selectedNegotiationId]);

  const handleOpened = (negotiationId: string) => {
    const row = rows.find((r) => r.negotiationId === negotiationId);
    const addedLocally = Boolean(row?.unread && !locallyRead.has(negotiationId));
    if (addedLocally) {
      setLocallyRead((prev) => new Set(prev).add(negotiationId));
    }
    markRead.mutate(negotiationId, {
      onError: () => {
        if (!addedLocally) return;
        setLocallyRead((prev) => {
          if (!prev.has(negotiationId)) return prev;
          const next = new Set(prev);
          next.delete(negotiationId);
          return next;
        });
      },
    });
  };

  const handleMarkAllRead = () => {
    if (globalUnreadCount <= 0) return;
    const addedIds = unreadRows.map((row) => row.negotiationId);
    if (addedIds.length > 0) {
      setLocallyRead((prev) => {
        const next = new Set(prev);
        for (const id of addedIds) next.add(id);
        return next;
      });
    }
    markAllRead.mutate(undefined, {
      onError: () => {
        if (addedIds.length === 0) return;
        setLocallyRead((prev) => {
          const next = new Set(prev);
          for (const id of addedIds) next.delete(id);
          return next;
        });
      },
    });
  };

  return (
    <Box display="flex" bg="bg.surface" overflow="hidden" flex={1} minH={0}>
      <Flex
        direction="column"
        w="360px"
        flexShrink={0}
        borderRightWidth="1px"
        borderColor="border.subtle"
        minH={0}
      >
        <VStack
          align="stretch"
          spacing={3}
          p={3}
          borderBottomWidth="1px"
          borderColor="border.subtle"
          flexShrink={0}
        >
          <HStack justify="space-between">
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
              Negotiations
            </Text>
            <Tooltip
              label={globalUnreadCount > 0 ? 'Mark all read' : 'No unread messages'}
              openDelay={300}
            >
              <IconButton
                aria-label="Mark all read"
                size="sm"
                variant="ghostGray"
                isDisabled={globalUnreadCount <= 0}
                icon={<Icon name="checks" size="md" />}
                onClick={handleMarkAllRead}
              />
            </Tooltip>
          </HStack>
          <TextField
            size="sm"
            placeholder="Search negotiations…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            h="32px"
          />
        </VStack>

        <Box flex={1} overflowY="auto" minH={0}>
          {isLoading ? (
            <Center py={10}>
              <Spinner color="brand.solid" />
            </Center>
          ) : isFeedError && rows.length === 0 ? (
            <Center py={10} px={4}>
              <VStack spacing={1}>
                <Text fontSize="sm" fontWeight="semibold" color="fg.default" textAlign="center">
                  Couldn't load negotiations
                </Text>
                <Text fontSize="xs" color="fg.muted" textAlign="center">
                  Refresh the page to try again
                </Text>
              </VStack>
            </Center>
          ) : rows.length === 0 ? (
            <Center py={10} px={4}>
              <VStack spacing={2}>
                <Icon name="message" size="xl" color="fg.muted" />
                <Text fontSize="sm" color="fg.muted" textAlign="center">
                  No negotiations yet
                </Text>
              </VStack>
            </Center>
          ) : (
            rows.map((row) => {
              const active = selectedNegotiationId === row.negotiationId;
              const unread = isUnread(row);
              return (
                <Box
                  key={row.negotiationId}
                  as="button"
                  textAlign="left"
                  w="100%"
                  px={3}
                  py={2.5}
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  borderLeftWidth="3px"
                  borderLeftColor={active ? 'brand.solid' : 'transparent'}
                  bg={active ? 'brand.subtle' : 'bg.surface'}
                  _hover={{ bg: active ? 'brand.subtle' : 'bg.subtle' }}
                  onClick={() => onSelectNegotiation(row.negotiationId)}
                >
                  <HStack justify="space-between" align="start" spacing={2}>
                    <VStack align="start" spacing={0.5} minW={0} flex={1}>
                      <HStack spacing={2} minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight={unread ? 'bold' : 'semibold'}
                          color="fg.default"
                          noOfLines={1}
                        >
                          {row.supplierText || 'Supplier'}
                        </Text>
                        {unread && (
                          <Box w="8px" h="8px" borderRadius="full" bg="error.solid" flexShrink={0} />
                        )}
                      </HStack>
                      <Text fontSize="xs" color="fg.muted" noOfLines={1}>
                        Inv # {row.invoiceNumber || '—'}
                      </Text>
                      <Text fontSize="xs" color="fg.muted" noOfLines={1}>
                        {row.preview || '—'}
                      </Text>
                    </VStack>
                    <Text fontSize="11px" color="fg.muted" whiteSpace="nowrap" flexShrink={0}>
                      {formatFeedDate(row.lastActivityAt)}
                    </Text>
                  </HStack>
                </Box>
              );
            })
          )}
          {!isLoading && hasNextPage && (
            <Button
              w="100%"
              size="sm"
              borderRadius={0}
              variant="solid"
              colorScheme="brand"
              py={6}
              isLoading={isFetchingNextPage}
              loadingText="Loading"
              onClick={() => void fetchNextPage()}
            >
              See more
            </Button>
          )}
        </Box>
      </Flex>

      <Box flex={1} minW={0} minH={0}>
        <NegotiationThreadPane
          negotiationId={selectedNegotiationId}
          onOpened={handleOpened}
        />
      </Box>
    </Box>
  );
}
